import React, { useMemo, useCallback } from 'react';
import {
  Calendar,
  momentLocalizer,
  View,
  SlotInfo,
} from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/de';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { 
  Appointment, 
  AppointmentStatus,
  AppointmentType 
} from '../../../lib/interfaces/appointment.interface';
import {
  Phone,
  MapPin,
  Video,
  Users,
  MoreHorizontal,
} from 'lucide-react';

moment.locale('de');
const localizer = momentLocalizer(moment);

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  resource: Appointment;
}

interface CalendarViewProps {
  appointments: Appointment[];
  onSelectSlot: (slotInfo: SlotInfo) => void;
  onSelectEvent: (appointment: Appointment) => void;
  onEventDrop?: (data: { event: CalendarEvent; start: Date; end: Date }) => void;
  onEventResize?: (data: { event: CalendarEvent; start: Date; end: Date }) => void;
  currentView?: View;
  onViewChange?: (view: View) => void;
  currentDate?: Date;
  onNavigate?: (date: Date) => void;
}

const eventStyleGetter = (event: CalendarEvent) => {
  const appointment = event.resource;
  let backgroundColor = '#3174ad';
  let borderColor = '#265a8f';
  
  if (appointment.status === AppointmentStatus.DONE) {
    backgroundColor = '#10b981';
    borderColor = '#059669';
  } else if (appointment.status === AppointmentStatus.CANCELED) {
    backgroundColor = '#6b7280';
    borderColor = '#4b5563';
  } else {
    // Color by type
    switch (appointment.type) {
      case AppointmentType.CALL:
        backgroundColor = '#3b82f6';
        borderColor = '#2563eb';
        break;
      case AppointmentType.SITE_VISIT:
        backgroundColor = '#f59e0b';
        borderColor = '#d97706';
        break;
      case AppointmentType.VIDEO:
        backgroundColor = '#8b5cf6';
        borderColor = '#7c3aed';
        break;
      case AppointmentType.MEETING:
        backgroundColor = '#06b6d4';
        borderColor = '#0891b2';
        break;
    }
  }

  return {
    style: {
      backgroundColor,
      borderColor,
      borderWidth: '1px',
      borderStyle: 'solid',
    },
  };
};

const getTypeIcon = (type: AppointmentType) => {
  switch (type) {
    case AppointmentType.CALL:
      return Phone;
    case AppointmentType.SITE_VISIT:
      return MapPin;
    case AppointmentType.VIDEO:
      return Video;
    case AppointmentType.MEETING:
      return Users;
    default:
      return MoreHorizontal;
  }
};

export const CalendarView: React.FC<CalendarViewProps> = ({
  appointments,
  onSelectSlot,
  onSelectEvent,
  onEventDrop,
  onEventResize,
  currentView = 'week',
  onViewChange,
  currentDate = new Date(),
  onNavigate,
}) => {
  const events = useMemo<CalendarEvent[]>(() => {
    return appointments.map(appointment => ({
      id: appointment._id,
      title: appointment.title,
      start: new Date(appointment.start),
      end: new Date(appointment.end),
      allDay: appointment.allDay,
      resource: appointment,
    }));
  }, [appointments]);

  const handleSelectEvent = useCallback((event: CalendarEvent) => {
    onSelectEvent(event.resource);
  }, [onSelectEvent]);

  const handleEventDrop = useCallback(({ event, start, end }: any) => {
    if (onEventDrop && event.resource.status === AppointmentStatus.SCHEDULED) {
      onEventDrop({ event, start, end });
    }
  }, [onEventDrop]);

  const handleEventResize = useCallback(({ event, start, end }: any) => {
    if (onEventResize && event.resource.status === AppointmentStatus.SCHEDULED) {
      onEventResize({ event, start, end });
    }
  }, [onEventResize]);

  const EventComponent = ({ event }: { event: CalendarEvent }) => {
    const appointment = event.resource;
    const Icon = getTypeIcon(appointment.type);
    
    return (
      <div className="h-full flex flex-col p-1 text-xs">
        <div className="flex items-center gap-1">
          <Icon className="h-3 w-3" />
          <span className="font-medium truncate">{event.title}</span>
        </div>
        {appointment.dealName && (
          <div className="text-[10px] opacity-90 truncate">{appointment.dealName}</div>
        )}
        {appointment.location && currentView !== 'month' && (
          <div className="text-[10px] opacity-75 truncate">{appointment.location}</div>
        )}
      </div>
    );
  };

  const messages = {
    allDay: 'Ganztägig',
    previous: 'Zurück',
    next: 'Weiter',
    today: 'Heute',
    month: 'Monat',
    week: 'Woche',
    day: 'Tag',
    agenda: 'Agenda',
    date: 'Datum',
    time: 'Zeit',
    event: 'Termin',
    noEventsInRange: 'Keine Termine in diesem Zeitraum.',
    showMore: (total: number) => `+${total} weitere`,
  };

  const formats = {
    dateFormat: 'DD',
    dayFormat: 'dd DD.MM',
    weekdayFormat: 'ddd',
    selectRangeFormat: ({ start, end }: { start: Date; end: Date }) =>
      `${moment(start).format('HH:mm')} - ${moment(end).format('HH:mm')}`,
    eventTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) =>
      `${moment(start).format('HH:mm')} - ${moment(end).format('HH:mm')}`,
    timeGutterFormat: 'HH:mm',
    monthHeaderFormat: 'MMMM YYYY',
    dayHeaderFormat: 'dddd, DD. MMMM',
    dayRangeHeaderFormat: ({ start, end }: { start: Date; end: Date }) =>
      `${moment(start).format('DD. MMMM')} - ${moment(end).format('DD. MMMM YYYY')}`,
  };

  return (
    <div className="h-full bg-white rounded-lg shadow-sm">
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: '100%' }}
        onSelectSlot={onSelectSlot}
        onSelectEvent={handleSelectEvent}
        onEventDrop={handleEventDrop}
        onEventResize={handleEventResize}
        selectable
        resizable
        eventPropGetter={eventStyleGetter}
        components={{
          event: EventComponent,
        }}
        messages={messages}
        formats={formats}
        view={currentView}
        onView={onViewChange}
        date={currentDate}
        onNavigate={onNavigate}
        min={new Date(0, 0, 0, 7, 0, 0)}
        max={new Date(0, 0, 0, 19, 0, 0)}
        step={30}
        timeslots={2}
        culture="de"
      />
    </div>
  );
};
